import React, { useState } from "react";
import axios from "axios";
import { CheckOutlined } from "@ant-design/icons";
import { FETCH_ALL_EMAIL_API } from "@/constants/apiEndpoints";
import { useTranslation } from "react-i18next";

const Step = ({ title, desc }) => (
  <div className="flex items-start gap-4">
    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-white border border-black flex items-center justify-center mt-1">
      <CheckOutlined style={{ fontSize: 18, color: "black" }} />
    </span>
    <div>
      <div className="text-lg font-bold text-black">{title}</div>
      <div className="text-base text-gray-700">{desc}</div>
    </div>
  </div>
);

const ContactForm = ({ headingAs: Heading = "h2" }) => {
  const { t } = useTranslation("contactForm");

  // Form states
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [website, setWebsite] = useState("");
  const [budget, setBudget] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback("");

    // Basic validation
    if (!fullName || !email || !phone || !company || !message) {
      setFeedback(t("validation.fillAll"));
      return;
    }
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(email)) {
      setFeedback(t("validation.invalidEmail"));
      return;
    }

    setLoading(true);
    try {
      const payload = {
        fullName,
        email,
        phone,
        company,
        website,
        budget,
        message,
      };
      const res = await axios.post(`${FETCH_ALL_EMAIL_API}/send`, payload);

      if (res.status === 200) {
        setFeedback(t("feedback.success"));
        setFullName("");
        setEmail("");
        setPhone("");
        setCompany("");
        setWebsite("");
        setBudget("");
        setMessage("");
      } else {
        setFeedback(res?.data?.message || t("feedback.fail"));
      }
    } catch (err) {
      setFeedback(err?.response?.data?.message || t("feedback.fail"));
    }
    setLoading(false);
  };

  return (
    <section className="w-full flex justify-center px-4 md:px-0 py-12 bg-white">
      <div className="w-full max-w-7xl flex flex-col md:flex-row gap-8 md:gap-16">
        {/* LEFT: Headline and steps */}
        <div className="flex-1 flex flex-col justify-start text-black">
          <Heading className="text-4xl md:text-5xl font-bold leading-tight mb-4">
            {t("left.titleLine1")} <br className="hidden md:block" />{" "}
            {t("left.titleLine2")}
          </Heading>
          <p className="text-lg mb-8 text-gray-700">{t("left.desc")}</p>
          <div className="space-y-6">
            <Step title={t("steps.0.title")} desc={t("steps.0.desc")} />
            <Step title={t("steps.1.title")} desc={t("steps.1.desc")} />
          </div>
        </div>

        {/* RIGHT: Form */}
        <div className="flex-1 flex items-center">
          <div className="bg-white rounded-2xl shadow-xl border border-black/10 w-full max-w-xl mx-auto p-8 md:p-10">
            <h3 className="text-2xl md:text-3xl font-bold text-black mb-7">
              {t("form.title")}{" "}
              <span className="font-extrabold">{t("form.titleAccent")}</span>
            </h3>

            <form
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
              onSubmit={handleSubmit}
              autoComplete="off"
            >
              <div className="flex flex-col">
                <label className="mb-1 text-sm font-medium text-black">
                  {t("form.fullName")} <span className="text-black">*</span>
                </label>
                <input
                  className="rounded-lg px-4 py-2 bg-[#fafbfc] border border-black/20 text-black placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black"
                  placeholder={t("placeholder.fullName")}
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </div>

              <div className="flex flex-col">
                <label className="mb-1 text-sm font-medium text-black">
                  {t("form.email")} <span className="text-black">*</span>
                </label>
                <input
                  type="email"
                  className="rounded-lg px-4 py-2 bg-[#fafbfc] border border-black/20 text-black placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="flex flex-col">
                <label className="mb-1 text-sm font-medium text-black">
                  {t("form.phone")} *
                </label>
                <input
                  className="rounded-lg px-4 py-2 bg-[#fafbfc] border border-black/20 text-black placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>

              <div className="flex flex-col">
                <label className="mb-1 text-sm font-medium text-black">
                  {t("form.company")} *
                </label>
                <input
                  className="rounded-lg px-4 py-2 bg-[#fafbfc] border border-black/20 text-black placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black"
                  required
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                />
              </div>

              <div className="flex flex-col md:col-span-2">
                <label className="mb-1 text-sm font-medium text-black">
                  {t("form.website")}
                </label>
                <input
                  className="rounded-lg px-4 py-2 bg-[#fafbfc] border border-black/20 text-black placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>

              <div className="flex flex-col">
                <label className="mb-1 text-sm font-medium text-black">
                  {t("form.budget")}
                </label>
                <select
                  className="rounded-lg px-4 py-2 bg-[#fafbfc] border border-black/20 text-black focus:outline-none focus:ring-2 focus:ring-black"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                >
                  <option value="">{t("budgetOptions.placeholder")}</option>
                  <option>{t("budgetOptions.lt50")}</option>
                  <option>{t("budgetOptions.between50_200")}</option>
                  <option>{t("budgetOptions.gt200")}</option>
                </select>
              </div>

              <div className="flex flex-col md:col-span-2">
                <label className="mb-1 text-sm font-medium text-black">
                  {t("form.message")} *
                </label>
                <textarea
                  rows={3}
                  className="rounded-lg px-4 py-2 bg-[#fafbfc] border border-black/20 text-black placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black"
                  required
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>

              <div className="md:col-span-2 mt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full rounded-xl bg-black hover:bg-gray-900 text-white font-bold text-lg py-3 shadow-lg transition ${
                    loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                  }`}
                >
                  {loading ? t("cta.sending") : t("cta.getStarted")}
                </button>

                {feedback && (
                  <div
                    className={`mt-4 text-center text-base font-medium ${
                      feedback.includes(t("feedback.successProbe"))
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {feedback}
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
